import { useCallback, useState } from 'react'
import { EntityDetail } from '@user/store/map/typings'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useAppSelector } from '@user/store/hooks/useAppSelector'
import {
  selectServiceLocationsDetails,
  setEntityDetails,
  setSelectedServiceLocationsDetails,
} from '@user/store/map/slice'
import {
  clearCurrentKnockStatus,
  updateKnockStatusHistoryById,
} from '@user/store/knockStatus/api'
import { toast } from '@sales-center/components'
import { buildKnockStatusReasonPatches } from '@user/utils/knockStatusReasonPatch'

type Props = {
  entityDetails: EntityDetail | null
  isDraftLead: boolean
  entityType: string
  knockStatusLocationId: number | null
  knockStatusHistoryId: number | null
  selectedServiceLocationId: number
  onRefresh?: () => void
}

export function useHandlers({
  entityDetails,
  isDraftLead,
  entityType,
  knockStatusLocationId,
  knockStatusHistoryId,
  selectedServiceLocationId,
  onRefresh,
}: Props) {
  const dispatch = useAppDispatch()
  const serviceLocationsDetails = useAppSelector(selectServiceLocationsDetails)
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const [isSavingReason, setIsSavingReason] = useState(false)

  const openClearConfirm = useCallback(() => {
    setClearConfirmOpen(true)
  }, [])

  const closeClearConfirm = useCallback(() => {
    setClearConfirmOpen(false)
  }, [])

  // Clears the active knock status (SH-661). History, notes, and timeline are kept. The
  // KnockStatusDetails shell (id + count) is kept and only the active status/reason are blanked,
  // so the card renders '-' immediately AND keeps a working "View History" link; the backend
  // keeps the entity FK so this survives a reopen re-fetch.
  const handleClearKnockStatus = useCallback(async () => {
    if (!knockStatusLocationId || !entityDetails) return
    setIsClearing(true)
    try {
      await dispatch(
        clearCurrentKnockStatus({
          locationId: knockStatusLocationId,
          entityType,
        }),
      ).unwrap()

      if (!isDraftLead) {
        dispatch(
          setEntityDetails({
            ...entityDetails,
            locations: {
              ...entityDetails.locations,
              serviceLocations: entityDetails.locations.serviceLocations.map(
                (loc) => {
                  if (
                    loc.id === selectedServiceLocationId &&
                    loc.knockStatusDetails
                  ) {
                    return {
                      ...loc,
                      knockStatusDetails: {
                        ...loc.knockStatusDetails,
                        knockStatusSettingsName: '',
                        mostRecentNote: '',
                      },
                    }
                  }
                  return loc
                },
              ),
            },
          }),
        )
      } else if (entityDetails.knockStatusDetails) {
        dispatch(
          setEntityDetails({
            ...entityDetails,
            knockStatusDetails: {
              ...entityDetails.knockStatusDetails,
              knockStatusSettingsName: '',
              mostRecentNote: '',
            },
          }),
        )
      }

      toast.success('Knock status cleared')
      setClearConfirmOpen(false)
      onRefresh?.()
    } catch (e: any) {
      toast.error(e?.message || 'Failed to clear knock status')
    } finally {
      setIsClearing(false)
    }
  }, [
    knockStatusLocationId,
    entityDetails,
    entityType,
    isDraftLead,
    selectedServiceLocationId,
    dispatch,
    onRefresh,
  ])

  const handleSaveReason = useCallback(
    async (newReason: string | null) => {
      if (!knockStatusHistoryId || !entityDetails) return false
      setIsSavingReason(true)
      try {
        await dispatch(
          updateKnockStatusHistoryById({
            id: knockStatusHistoryId,
            entityType,
            input: {
              customReason: newReason,
              knockStatusName: null,
              previousKnockStatusName: null,
              entityType,
              entityId: entityDetails.id,
            },
          }),
        ).unwrap()

        // Patches BOTH the single-entity entityDetails slice AND the
        // multi-tenant selectedServiceLocationsDetails slice, so if a user
        // has this drawer and the multi-tenant list open at once for the
        // same record, both stay in sync live rather than one going stale
        // until its next refetch.
        const { patchedEntityDetails, patchedServiceLocationsDetails } =
          buildKnockStatusReasonPatches({
            entityId: entityDetails.id,
            serviceLocationId: selectedServiceLocationId,
            isDraftLead,
            newReason,
            entityDetails,
            serviceLocationsDetails: serviceLocationsDetails.locations,
            knockStatusLocationId,
          })

        if (patchedEntityDetails) {
          dispatch(setEntityDetails(patchedEntityDetails))
        }
        if (patchedServiceLocationsDetails) {
          dispatch(
            setSelectedServiceLocationsDetails(patchedServiceLocationsDetails),
          )
        }

        toast.success('Reason updated')
        // Deliberately no onRefresh() here, unlike handleDeleteKnockStatus above.
        // onRefresh triggers a real GET /Lead|Opportunity/{id} re-fetch that reads
        // from the read replica, which can lag behind the write just committed by
        // updateKnockStatusHistoryById -- that stale re-fetch would silently
        // overwrite the correct optimistic patch above with the pre-edit reason.
        // The optimistic patches above are already complete for this action
        // (unlike delete, which also needs a map-pin visual refresh).
        return true
      } catch (e: any) {
        toast.error(e?.message || 'Failed to update reason')
        return false
      } finally {
        setIsSavingReason(false)
      }
    },
    [
      knockStatusHistoryId,
      entityDetails,
      entityType,
      isDraftLead,
      selectedServiceLocationId,
      serviceLocationsDetails,
      knockStatusLocationId,
      dispatch,
    ],
  )

  return {
    clearConfirmOpen,
    isClearing,
    isSavingReason,
    openClearConfirm,
    closeClearConfirm,
    handleClearKnockStatus,
    handleSaveReason,
  }
}
